import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, formatPrice, apiErr, imageUrl, uiText } from "@/lib/api";
import { CheckCircle2, Clock, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";

const MANUAL = { nequi: "Nequi", bancolombia: "Transferencia Bancolombia", llave: "Llave Bancolombia (Bre-B)" };
const QR_KEYS = { nequi: "nequi_qr", bancolombia: "bancolombia_qr", llave: "llave_qr" };

export default function OrderResultPage() {
    const { id } = useParams();
    const [uploading, setUploading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const { data: order, isLoading, refetch } = useQuery({
        queryKey: ["order", id],
        queryFn: async () => (await api.get(`/orders/${id}`)).data,
        refetchInterval: 5000,
    });
    const { data: settings } = useQuery({ queryKey: ["settings-order"], queryFn: async () => (await api.get("/settings")).data });

    const uploadReceipt = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/orders/${id}/receipt`, fd);
            toast.success(uiText(settings, "toast_receipt_ok"));
            refetch();
        } catch (e2) { toast.error(apiErr(e2)); } finally { setUploading(false); }
    };

    const cancelOrder = async () => {
        if (!window.confirm("¿Seguro que querés cancelar el pedido? Esta acción no se puede deshacer.")) return;
        setCancelling(true);
        try {
            await api.post(`/orders/${id}/cancel`);
            toast.success(uiText(settings, "toast_order_cancelled"));
            refetch();
        } catch (e2) { toast.error(apiErr(e2)); } finally { setCancelling(false); }
    };
    if (isLoading) return <div className="py-24 text-center text-sm text-ink/50">Consultando pedido…</div>;
    if (!order) return <div className="py-24 text-center">Pedido no encontrado.</div>;

    const paid = order.status === "paid";
    const cod = ["cod", "whatsapp"].includes(order.payment_method);
    const isManual = !!MANUAL[order.payment_method];
    const verifying = order.payment_status === "verifying";
    const cancelled = order.status === "cancelled";
    const cancellable = !cancelled && !paid && ["awaiting_receipt", "pending"].includes(order.payment_status);
    const waLink = (() => {
        if (order.payment_method !== "whatsapp" || !settings?.whatsapp) return "";
        const lines = order.items.map((i) => `• ${i.name} (Talla ${i.size} · ${i.color}) x${i.quantity} — ${formatPrice(i.price * i.quantity)}`).join("\n");
        const msg = `Hola! Quiero confirmar mi pedido en J&M American Shop:\n\n${lines}\n\nEnvío: ${formatPrice(order.shipping)}\nTOTAL: ${formatPrice(order.total)}\n\nNombre: ${order.customer?.name}\nCiudad: ${order.customer?.city}\nDirección: ${order.customer?.address}\nN° de pedido: ${order.id}`;
        return `https://wa.me/${String(settings.whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;
    })();
    return (
        <div className="mx-auto max-w-2xl px-4 sm:px-8 py-16">
            <div className="text-center">
                {cancelled ? <XCircle className="w-16 h-16 text-red-500 mx-auto" /> : paid ? <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" /> : <Clock className="w-16 h-16 text-terracotta mx-auto" />}
                <h1 className="font-display text-3xl mt-4">{cancelled ? "Pedido cancelado" : paid ? "¡Pedido confirmado!" : cod ? "Pedido registrado" : "Esperando confirmación"}</h1>
                <p className="text-sm text-ink/60 mt-2">
                    {cod ? "Nos pondremos en contacto para coordinar el pago y la entrega." : paid ? "Tu pago fue aprobado. Ya estamos preparando tu pedido." : isManual ? (verifying ? "Recibimos tu comprobante. El pedido queda en espera mientras verificamos el pago." : "Realizá el pago con los datos de abajo y subí el comprobante.") : "Tu pago está siendo procesado. Actualizaremos el estado cuando MercadoPago nos confirme."}
                </p>
                <p className="text-xs text-ink/50 mt-4">ID: {order.id}</p>
            </div>
            <div className="bg-white border border-sandLight rounded-lg p-6 mt-8">
                <p className="text-eyebrow mb-3">Detalle</p>
                <div className="space-y-2 text-sm">
                    {order.items.map((i, k) => (
                        <div key={k} className="flex justify-between"><span>{i.name} × {i.quantity}</span><span>{formatPrice(i.price * i.quantity)}</span></div>
                    ))}
                </div>
                <div className="border-t border-sandLight mt-4 pt-3 space-y-1 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
                    <div className="flex justify-between"><span>Envío</span><span>{formatPrice(order.shipping)}</span></div>
                    <div className="flex justify-between font-medium text-base mt-2"><span>TOTAL</span><span>{formatPrice(order.total)}</span></div>
                </div>
                <div className="border-t border-sandLight mt-4 pt-3 text-sm">
                    <p><span className="text-ink/60">Estado:</span> <span className="font-medium capitalize">{order.status}</span></p>
                    <p><span className="text-ink/60">Método:</span> {order.payment_method}</p>
                </div>
            </div>
            {waLink && !cancelled && (
                <div className="bg-white border border-sandLight rounded-lg p-6 mt-6 text-center">
                    <p className="text-sm text-ink/70 mb-4">Tu pedido ya está registrado. Completá la compra enviándolo por WhatsApp:</p>
                    <a href={waLink} target="_blank" rel="noreferrer" data-testid="btn-order-whatsapp" className="btn-ink inline-flex items-center gap-2">Enviar pedido por WhatsApp</a>
                </div>
            )}
            {isManual && !cancelled && (
                <div className="bg-white border border-sandLight rounded-lg p-6 mt-6">
                    <p className="text-eyebrow mb-3">Pago por {MANUAL[order.payment_method]}</p>
                    <div className="text-sm space-y-1 mb-4">
                        {order.payment_method === "nequi" && <p>Número Nequi: <b>{settings?.nequi_number || "—"}</b></p>}
                        {order.payment_method === "bancolombia" && <p>Cuenta Bancolombia: <b>{settings?.bancolombia_account || "—"}</b></p>}
                        {order.payment_method === "llave" && <p>Llave Bre-B: <b>{settings?.bancolombia_llave || "—"}</b></p>}
                        <p>Total a pagar: <b>{formatPrice(order.total)}</b></p>
                    </div>
                    {settings?.[QR_KEYS[order.payment_method]] && (
                        <div className="mb-4">
                            <p className="text-xs text-ink/60 mb-2">Escaneá el QR para pagar:</p>
                            <img src={imageUrl(settings[QR_KEYS[order.payment_method]])} alt="QR de pago" className="w-40 h-40 object-contain border border-sandLight rounded bg-white" data-testid="order-payment-qr" />
                        </div>
                    )}
                    {(order.payment_status === "awaiting_receipt" || order.payment_status === "rejected") && (
                        <label className="btn-ink inline-flex items-center gap-2 cursor-pointer" data-testid="btn-upload-receipt">
                            <input type="file" accept="image/*,.pdf" onChange={uploadReceipt} className="hidden" />
                            <Upload className="w-4 h-4" />{uploading ? "Subiendo…" : "Subir comprobante"}
                        </label>
                    )}
                    {verifying && <p className="text-sm text-terracotta" data-testid="receipt-verifying">Comprobante recibido. Pedido en espera de verificación del pago.</p>}
                    {order.payment_status === "paid" && <p className="text-sm text-emerald-700">Pago verificado. ¡Gracias por tu compra!</p>}
                    {order.payment_status === "rejected" && <p className="text-sm text-red-600 mt-2">El comprobante anterior fue rechazado. Podés subir uno nuevo.</p>}
                </div>
            )}
            {cancellable && (
                <div className="text-center mt-6">
                    <button onClick={cancelOrder} disabled={cancelling} data-testid="btn-cancel-order" className="text-xs uppercase tracking-wide text-red-600 underline underline-offset-4 hover:text-red-800 disabled:opacity-50">
                        {cancelling ? "Cancelando…" : "Cancelar pedido"}
                    </button>
                </div>
            )}
            <div className="text-center mt-8">
                <Link to="/tienda" className="btn-outline">Seguir comprando</Link>
            </div>
        </div>
    );
}
